/**
 * CapabilityGate Component
 * Conditionally renders children based on organization capability status
 *
 * Usage:
 * <CapabilityGate organizationId={orgId} capability="communications">
 *   <CommunicationFeature />
 * </CapabilityGate>
 *
 * With fallback:
 * <CapabilityGate
 *   organizationId={orgId}
 *   capability="communications"
 *   fallback={<UpgradePrompt />}
 * >
 *   <CommunicationFeature />
 * </CapabilityGate>
 */

import React, { ReactNode } from 'react';
import { useCapability } from '@/hooks/useCapabilities';
import { Lock, AlertCircle } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface CapabilityGateProps {
  /** The organization ID to check capabilities for */
  organizationId: number | null;

  /** The capability name to check (e.g., 'communications', 'care_referrals') */
  capability: string;

  /** Content to render when capability is enabled */
  children: ReactNode;

  /** Content to render when capability is disabled (optional) */
  fallback?: ReactNode;

  /** Show loading state while checking capability */
  showLoading?: boolean;

  /** Behavior when capability is disabled */
  behavior?: 'hide' | 'disable' | 'show-upgrade';

  /** Custom upgrade message */
  upgradeMessage?: string;

  /** Callback when upgrade prompt is clicked */
  onUpgradeClick?: () => void;
}

export function CapabilityGate({
  organizationId,
  capability,
  children,
  fallback,
  showLoading = true,
  behavior,
  upgradeMessage = 'This feature is not enabled for your organization.',
  onUpgradeClick
}: CapabilityGateProps) {
  const { t } = useAppTranslation();
  const { enabled, config, loading, error } = useCapability(organizationId, capability);

  // Use behavior from config if not explicitly provided via props
  // Priority: explicit prop > config value > default 'hide'
  const effectiveBehavior = behavior ?? (config?.display_behavior as 'hide' | 'disable' | 'show-upgrade') ?? 'hide';

  // Show loading state
  if (loading && showLoading) {
    return (
      <div className="capability-gate-loading">
        <div className="spinner"></div>
        <p>{t('common.loading', 'Loading...')}</p>
        <style jsx>{`
          .capability-gate-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 24px;
            gap: 12px;
          }
          .spinner {
            width: 24px;
            height: 24px;
            border: 3px solid #e5e7eb;
            border-top-color: #6366f1;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .capability-gate-loading p {
            margin: 0;
            color: #6b7280;
            font-size: 14px;
          }
        `}</style>
      </div>
    );
  }

  // Show error state (but don't block - default to disabled)
  if (error) {
    console.error(`CapabilityGate error for ${capability}:`, error);
    // Fall through to disabled state
  }

  // If capability is enabled, render children
  if (enabled) {
    return <>{children}</>;
  }

  // Capability is disabled - handle based on behavior
  switch (effectiveBehavior) {
    case 'hide':
      // Don't render anything
      return fallback ? <>{fallback}</> : null;

    case 'disable':
      // Render children but make them visually disabled
      return (
        <div className="capability-gate-disabled">
          <div className="disabled-overlay">
            <Lock className="lock-icon" />
            <p>{upgradeMessage}</p>
            {onUpgradeClick && (
              <button onClick={onUpgradeClick} className="upgrade-button">
                {t('common.enable_feature', 'Enable Feature')}
              </button>
            )}
          </div>
          <div className="disabled-content">
            {children}
          </div>
          <style jsx>{`
            .capability-gate-disabled {
              position: relative;
              pointer-events: none;
              opacity: 0.5;
            }
            .disabled-overlay {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              background: rgba(255, 255, 255, 0.9);
              backdrop-filter: blur(4px);
              z-index: 10;
              gap: 12px;
              padding: 24px;
              pointer-events: auto;
            }
            .lock-icon {
              width: 48px;
              height: 48px;
              color: #6b7280;
            }
            .disabled-overlay p {
              margin: 0;
              color: #374151;
              font-size: 14px;
              text-align: center;
              max-width: 300px;
            }
            .upgrade-button {
              padding: 8px 16px;
              background: #6366f1;
              color: white;
              border: none;
              border-radius: 6px;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              transition: background 0.2s;
            }
            .upgrade-button:hover {
              background: #4f46e5;
            }
            .disabled-content {
              filter: grayscale(100%);
            }
          `}</style>
        </div>
      );

    case 'show-upgrade':
      // Show upgrade prompt instead of children
      return (
        <div className="capability-gate-upgrade">
          <div className="upgrade-card">
            <Lock className="upgrade-icon" />
            <h3>{t('common.feature_not_enabled', 'Feature Not Enabled')}</h3>
            <p>{upgradeMessage}</p>
            {onUpgradeClick && (
              <button onClick={onUpgradeClick} className="upgrade-button">
                {t('common.enable_this_feature', 'Enable This Feature')}
              </button>
            )}
          </div>
          <style jsx>{`
            .capability-gate-upgrade {
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 48px 24px;
            }
            .upgrade-card {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 16px;
              max-width: 400px;
              padding: 32px;
              background: white;
              border: 2px solid #e5e7eb;
              border-radius: 12px;
              text-align: center;
            }
            .upgrade-icon {
              width: 64px;
              height: 64px;
              color: #6366f1;
            }
            .upgrade-card h3 {
              margin: 0;
              font-size: 20px;
              font-weight: 600;
              color: #111827;
            }
            .upgrade-card p {
              margin: 0;
              color: #6b7280;
              font-size: 14px;
              line-height: 1.5;
            }
            .upgrade-button {
              padding: 12px 24px;
              background: #6366f1;
              color: white;
              border: none;
              border-radius: 8px;
              font-size: 16px;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s;
            }
            .upgrade-button:hover {
              background: #4f46e5;
              transform: translateY(-1px);
              box-shadow: 0 4px 6px rgba(99, 102, 241, 0.2);
            }
          `}</style>
        </div>
      );

    default:
      return fallback ? <>{fallback}</> : null;
  }
}

/**
 * Hook version of CapabilityGate for use in component logic
 */
export const useCapabilityGate = (
  organizationId: number | null,
  capability: string
) => {
  const { enabled, loading, error } = useCapability(organizationId, capability);
  return { isEnabled: enabled, isLoading: loading, error };
};

export default CapabilityGate;
